import { FormStructure, FormTheme } from '@/features/application/types/forms';
import { US_STATES, COUNTRIES } from './address-options';
import { COMPANY_TYPES } from './company-types';

// Default theme for new form mappings
export const defaultTheme: FormTheme = {
  primary: '#0F172A', // Slate-900 for primary background
  accent: '#FFFFFF', // White for accents
  background: '#f2eeee', // Light gray for content background
  formBackground: '#7e7ea0', // Dark blue-gray for form background
};

// Initial form structure template for new field mappings
// Page 1: Basics - essential info to create Lead quickly
// Page 2: Owners - owner-related fields
// Page 3: Business - company details, address, and funding
// Page 4: E-Sign
// Page 5: Documents
export const initialFormStructure: FormStructure = {
  logo: null,
  pages: [
    {
      id: 'basics',
      title: 'Basics',
      sections: [
        {
          id: 'basic-contact-information',
          title: 'Tell us about yourself',
          columns: 2,
          fields: [
            {
              id: 'owner1FirstName',
              type: 'text',
              label: 'Primary Owner First Name',
              value: '',
              placeholder: 'John',
              required: true,
              displayName: 'First Name',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1],
              },
              path: [],
            },
            {
              id: 'owner1LastName',
              type: 'text',
              label: 'Primary Owner Last Name',
              value: '',
              placeholder: 'Smith',
              required: true,
              displayName: 'Last Name',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1],
              },
              path: [],
            },
            {
              id: 'companyName',
              type: 'text',
              label: 'Company Name',
              value: '',
              placeholder: 'Test Company Inc.',
              required: true,
              displayName: 'Company Name',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              path: [],
            },
            {
              id: 'owner1Email',
              type: 'email',
              label: 'Primary Owner Email',
              value: '',
              placeholder: 'john.smith@example.com',
              required: true,
              displayName: 'Email',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1],
              },
              path: [],
            },
          ],
        },
      ],
    },
    {
      id: 'owner-information',
      title: 'Owners',
      sections: [
        {
          id: 'owner-contact-information',
          title: 'Owner Contact Information',
          columns: 2,
          fields: [
            {
              id: 'owner1Phone',
              type: 'phone',
              label: 'Primary Owner Phone',
              value: '',
              placeholder: '555-123-4567',
              required: true,
              displayName: 'Phone',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 10,
              permissions: {
                canEdit: [1],
                canView: [1],
              },
              path: [],
            },
            {
              id: 'owner2FirstName',
              type: 'text',
              label: 'Second Owner First Name',
              value: '',
              placeholder: 'Jane',
              required: true,
              displayName: 'First Name',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [2],
                canView: [2],
              },
              path: [],
            },
            {
              id: 'owner2LastName',
              type: 'text',
              label: 'Second Owner Last Name',
              value: '',
              placeholder: 'Doe',
              required: true,
              displayName: 'Last Name',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [2],
                canView: [2],
              },
              path: [],
            },
            {
              id: 'owner2Email',
              type: 'email',
              label: 'Second Owner Email',
              value: '',
              placeholder: 'jane.doe@example.com',
              required: true,
              displayName: 'Email',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [2],
                canView: [2],
              },
              path: [],
            },
            {
              id: 'owner2Phone',
              type: 'phone',
              label: 'Second Owner Phone',
              value: '',
              placeholder: '555-987-6543',
              required: true,
              displayName: 'Phone',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 10,
              permissions: {
                canEdit: [2],
                canView: [2],
              },
              path: [],
            },
            {
              id: 'owner3FirstName',
              type: 'text',
              label: 'Third Owner First Name',
              value: '',
              placeholder: 'Bob',
              required: true,
              displayName: 'First Name',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [3],
                canView: [3],
              },
              path: [],
            },
            {
              id: 'owner3LastName',
              type: 'text',
              label: 'Third Owner Last Name',
              value: '',
              placeholder: 'Johnson',
              required: true,
              displayName: 'Last Name',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [3],
                canView: [3],
              },
              path: [],
            },
            {
              id: 'owner3Email',
              type: 'email',
              label: 'Third Owner Email',
              value: '',
              placeholder: 'bob.johnson@example.com',
              required: true,
              displayName: 'Email',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [3],
                canView: [3],
              },
              path: [],
            },
            {
              id: 'owner3Phone',
              type: 'phone',
              label: 'Third Owner Phone',
              value: '',
              placeholder: '555-234-5678',
              required: true,
              displayName: 'Phone',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 10,
              permissions: {
                canEdit: [3],
                canView: [3],
              },
              path: [],
            },
          ],
        },
        {
          id: 'owner-personal-details',
          title: 'Owner Personal Details',
          columns: 2,
          fields: [
            {
              id: 'owner1OwnershipPercentage',
              type: 'number',
              label: 'Primary Owner Ownership %',
              value: '',
              placeholder: '60',
              required: true,
              displayName: 'Ownership %',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 3,
              permissions: {
                canEdit: [1],
                canView: [1],
              },
              path: [],
            },
            {
              id: 'owner1DOB',
              type: 'date',
              label: 'Primary Owner Date of Birth',
              value: '',
              placeholder: '1980-01-01',
              required: true,
              displayName: 'Date of Birth',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 10,
              permissions: {
                canEdit: [1],
                canView: [1],
              },
              path: [],
            },
            {
              id: 'owner1SSN',
              type: 'ssn',
              label: 'Primary Owner Social Security Number',
              value: '',
              placeholder: 123456789,
              required: true,
              displayName: 'SSN',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 9,
              permissions: {
                canEdit: [1],
                canView: [1],
              },
              path: [],
            },
            {
              id: 'owner2OwnershipPercentage',
              type: 'number',
              label: 'Second Owner Ownership %',
              value: '',
              placeholder: '20',
              required: true,
              displayName: 'Ownership %',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 3,
              permissions: {
                canEdit: [2],
                canView: [2],
              },
              path: [],
            },
            {
              id: 'owner2DOB',
              type: 'date',
              label: 'Second Owner Date of Birth',
              value: '',
              placeholder: '1985-01-01',
              required: true,
              displayName: 'Date of Birth',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 10,
              permissions: {
                canEdit: [2],
                canView: [2],
              },
              path: [],
            },
            {
              id: 'owner2SSN',
              type: 'ssn',
              label: 'Second Owner Social Security Number',
              value: '',
              placeholder: 987654321,
              required: true,
              displayName: 'SSN',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 9,
              permissions: {
                canEdit: [2],
                canView: [2],
              },
              path: [],
            },
            {
              id: 'owner3OwnershipPercentage',
              type: 'number',
              label: 'Third Owner Ownership %',
              value: '',
              placeholder: '20',
              required: true,
              displayName: 'Ownership %',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 3,
              permissions: {
                canEdit: [3],
                canView: [3],
              },
              path: [],
            },
            {
              id: 'owner3DOB',
              type: 'date',
              label: 'Third Owner Date of Birth',
              value: '',
              placeholder: '2000-01-01',
              required: true,
              displayName: 'Date of Birth',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 10,
              permissions: {
                canEdit: [3],
                canView: [3],
              },
              path: [],
            },
            {
              id: 'owner3SSN',
              type: 'ssn',
              label: 'Third Owner Social Security Number',
              value: '',
              placeholder: 123456789,
              required: true,
              displayName: 'SSN',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 9,
              permissions: {
                canEdit: [3],
                canView: [3],
              },
              path: [],
            },
          ],
        },
        {
          id: 'owner-addresses',
          title: 'Owner Addresses',
          columns: 2,
          fields: [
            {
              id: 'owner1Address',
              type: 'text',
              label: 'Primary Owner Street Address',
              value: '',
              placeholder: '123 Main Street',
              required: true,
              displayName: 'Street Address',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1],
              },
              path: [],
            },
            {
              id: 'owner1City',
              type: 'text',
              label: 'Primary Owner City',
              value: '',
              placeholder: 'San Francisco',
              required: true,
              displayName: 'City',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1],
              },
              path: [],
            },
            {
              id: 'owner1State',
              type: 'picklist',
              label: 'Primary Owner State',
              value: '',
              placeholder: 'Select a state...',
              required: true,
              displayName: 'State',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1],
              },
              originalPicklistOptions: US_STATES,
              currentPicklistOptions: US_STATES,
              path: [],
            },
            {
              id: 'owner1Zip',
              type: 'text',
              label: 'Primary Owner ZIP Code',
              value: '',
              placeholder: '94105',
              required: true,
              displayName: 'ZIP Code',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 5,
              permissions: {
                canEdit: [1],
                canView: [1],
              },
              path: [],
            },
            {
              id: 'owner1Country',
              type: 'picklist',
              label: 'Primary Owner Country',
              value: '',
              placeholder: 'Select a country...',
              required: true,
              displayName: 'Country',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1],
              },
              originalPicklistOptions: COUNTRIES,
              currentPicklistOptions: COUNTRIES,
              path: [],
            },
            {
              id: 'owner2Address',
              type: 'text',
              label: 'Second Owner Street Address',
              value: '',
              placeholder: '123 Main Street',
              required: true,
              displayName: 'Street Address',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [2],
                canView: [2],
              },
              path: [],
            },
            {
              id: 'owner2City',
              type: 'text',
              label: 'Second Owner City',
              value: '',
              placeholder: 'San Francisco',
              required: true,
              displayName: 'City',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [2],
                canView: [2],
              },
              path: [],
            },
            {
              id: 'owner2State',
              type: 'picklist',
              label: 'Second Owner State',
              value: '',
              placeholder: 'Select a state...',
              required: true,
              displayName: 'State',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [2],
                canView: [2],
              },
              originalPicklistOptions: US_STATES,
              currentPicklistOptions: US_STATES,
              path: [],
            },
            {
              id: 'owner2Zip',
              type: 'text',
              label: 'Second Owner ZIP Code',
              value: '',
              placeholder: '94105',
              required: true,
              displayName: 'ZIP Code',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 5,
              permissions: {
                canEdit: [2],
                canView: [2],
              },
              path: [],
            },
            {
              id: 'owner2Country',
              type: 'picklist',
              label: 'Second Owner Country',
              value: '',
              placeholder: 'Select a country...',
              required: true,
              displayName: 'Country',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [2],
                canView: [2],
              },
              originalPicklistOptions: COUNTRIES,
              currentPicklistOptions: COUNTRIES,
              path: [],
            },
            {
              id: 'owner3Address',
              type: 'text',
              label: 'Third Owner Street Address',
              value: '',
              placeholder: '123 Main Street',
              required: true,
              displayName: 'Street Address',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [3],
                canView: [3],
              },
              path: [],
            },
            {
              id: 'owner3City',
              type: 'text',
              label: 'Third Owner City',
              value: '',
              placeholder: 'San Francisco',
              required: true,
              displayName: 'City',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [3],
                canView: [3],
              },
              path: [],
            },
            {
              id: 'owner3State',
              type: 'picklist',
              label: 'Third Owner State',
              value: '',
              placeholder: 'Select a state...',
              required: true,
              displayName: 'State',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [3],
                canView: [3],
              },
              originalPicklistOptions: US_STATES,
              currentPicklistOptions: US_STATES,
              path: [],
            },
            {
              id: 'owner3Zip',
              type: 'text',
              label: 'Third Owner ZIP Code',
              value: '',
              placeholder: '94105',
              required: true,
              displayName: 'ZIP Code',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 5,
              permissions: {
                canEdit: [3],
                canView: [3],
              },
              path: [],
            },
            {
              id: 'owner3Country',
              type: 'picklist',
              label: 'Third Owner Country',
              value: '',
              placeholder: 'Select a country...',
              required: true,
              displayName: 'Country',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [3],
                canView: [3],
              },
              originalPicklistOptions: COUNTRIES,
              currentPicklistOptions: COUNTRIES,
              path: [],
            },
            {
              id: 'page1CompleteTimestamp',
              type: 'datetime',
              label: 'Page 1 Complete Timestamp',
              value: '',
              placeholder: 'Set automatically when Owner Information is completed',
              required: false,
              displayName: 'Page 1 Complete Timestamp',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [],
                canView: [],
              },
              path: [],
            },
          ],
        },
      ],
    },
    {
      id: 'business-info',
      title: 'Business',
      sections: [
        {
          id: 'company-details',
          title: 'Company Details',
          columns: 2,
          fields: [
            {
              id: 'dba',
              type: 'text',
              label: 'DBA (Doing Business As)',
              value: '',
              placeholder: 'Business Trading Name',
              required: false,
              displayName: 'DBA (Doing Business As)',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              path: [],
            },
            {
              id: 'federalTaxId',
              type: 'tax-id',
              label: 'Federal Tax ID',
              value: '',
              placeholder: '12-3456789',
              required: true,
              displayName: 'Federal Tax ID',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 10,
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              path: [],
            },
            {
              id: 'companyType',
              type: 'picklist',
              label: 'Company Type',
              value: '',
              placeholder: 'Select company type...',
              required: true,
              displayName: 'Company Type',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              originalPicklistOptions: COMPANY_TYPES,
              currentPicklistOptions: COMPANY_TYPES,
              path: [],
            },
            {
              id: 'industryDescription',
              type: 'text',
              label: 'Industry Description',
              value: '',
              placeholder: 'Software Development',
              required: true,
              displayName: 'Industry Description',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              path: [],
            },
            {
              id: 'businessPhone',
              type: 'phone',
              label: 'Business Phone',
              value: '',
              placeholder: '555-123-4567',
              required: true,
              displayName: 'Business Phone',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 12,
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              path: [],
            },
            {
              id: 'fax',
              type: 'phone',
              label: 'Fax',
              value: '',
              placeholder: '555-765-4321',
              required: false,
              displayName: 'Fax',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 10,
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              path: [],
            },
            {
              id: 'businessStartDate',
              type: 'date',
              label: 'Business Start Date',
              value: '',
              placeholder: '2020-01-15',
              required: true,
              displayName: 'Business Start Date',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 10,
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              path: [],
            },
          ],
        },
        {
          id: 'business-address',
          title: 'Business Address',
          columns: 2,
          fields: [
            {
              id: 'businessAddress',
              type: 'text',
              label: 'Street Address',
              value: '',
              placeholder: '123 Main Street',
              required: true,
              displayName: 'Street Address',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              path: [],
            },
            {
              id: 'businessCity',
              type: 'text',
              label: 'City',
              value: '',
              placeholder: 'San Francisco',
              required: true,
              displayName: 'City',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              path: [],
            },
            {
              id: 'businessState',
              type: 'picklist',
              label: 'State',
              value: '',
              placeholder: 'Select a state...',
              required: true,
              displayName: 'State',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              originalPicklistOptions: US_STATES,
              currentPicklistOptions: US_STATES,
              path: [],
            },
            {
              id: 'businessZip',
              type: 'text',
              label: 'ZIP Code',
              value: '',
              placeholder: '94105',
              required: true,
              displayName: 'ZIP Code',
              fieldApiName: '',
              fieldLabel: '',
              formFieldMaxLength: 5,
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              path: [],
            },
            {
              id: 'businessCountry',
              type: 'picklist',
              label: 'Country',
              value: '',
              placeholder: 'Select a country...',
              required: true,
              displayName: 'Country',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              originalPicklistOptions: COUNTRIES,
              currentPicklistOptions: COUNTRIES,
              path: [],
            },
          ],
        },
        {
          id: 'funding-details',
          title: 'Funding Details',
          columns: 2,
          fields: [
            {
              id: 'annualRevenue',
              type: 'number',
              label: 'Annual Revenue',
              value: '',
              placeholder: '500000',
              required: true,
              displayName: 'Annual Revenue',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              path: [],
            },
            {
              id: 'loanAmount',
              type: 'number',
              label: 'Requested Loan Amount',
              value: '',
              placeholder: '50000',
              required: true,
              displayName: 'Requested Loan Amount',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              path: [],
            },
            {
              id: 'loanPurpose',
              type: 'text',
              label: 'Loan Purpose',
              value: '',
              placeholder: 'Business Expansion',
              required: true,
              displayName: 'Loan Purpose',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1, 2, 3],
              },
              path: [],
            },
            {
              id: 'page2CompleteTimestamp',
              type: 'datetime',
              label: 'Page 2 Complete Timestamp',
              value: '',
              placeholder: 'Set automatically when Business Information is completed',
              required: false,
              displayName: 'Page 2 Complete Timestamp',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [],
                canView: [],
              },
              path: [],
            },
          ],
        },
      ],
    },
    {
      id: 'signatures',
      title: 'E-Sign',
      sections: [
        {
          id: 'signatures',
          title: 'Signatures',
          columns: 1,
          fields: [
            {
              id: 'signature1',
              type: 'signature',
              label: 'Primary Signer Signature',
              value: '',
              required: true,
              displayName: 'Primary Signer Signature',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1],
              },
              path: [],
            },
            {
              id: 'signature2',
              type: 'signature',
              label: 'Second Signer Signature',
              value: '',
              required: true,
              displayName: 'Second Signer Signature',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [2],
                canView: [2],
              },
              path: [],
            },
            {
              id: 'signature3',
              type: 'signature',
              label: 'Third Signer Signature',
              value: '',
              required: true,
              displayName: 'Third Signer Signature',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [3],
                canView: [3],
              },
              path: [],
            },
            {
              id: 'applicationSubmittedTimestamp',
              type: 'datetime',
              label: 'Application Submitted Timestamp',
              value: '',
              placeholder: 'Set automatically when application is submitted',
              required: false,
              displayName: 'Application Submitted Timestamp',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [],
                canView: [],
              },
              path: [],
            },
          ],
        },
      ],
    },
    {
      id: 'document-upload',
      title: 'Documents',
      sections: [
        {
          id: 'document-upload',
          title: 'Document Upload',
          columns: 1,
          fields: [
            {
              id: 'bank-statements',
              type: 'file',
              label: 'Upload Bank Statements (last 4 months)',
              value: null,
              required: false,
              displayName: 'Upload Bank Statements',
              fieldApiName: '',
              fieldLabel: '',
              permissions: {
                canEdit: [1],
                canView: [1],
              },
              acceptedFileTypes: ['.pdf', '.jpg', '.png'],
              path: [],
            },
          ],
        },
      ],
    },
  ],
  theme: defaultTheme,
};
